import React, { useEffect, useState } from "react";
import { Team, Player, TransactionType } from "../objects/types";
import {
  listTeams,
  listPlayers,
  createTeam,
  createPlayer,
  createTransaction,
} from "../repository/api";
import {
  AdminUser,
  adminCreateUser,
  adminListUsers,
  adminSetUserActive,
} from "../repository/authRepository";

interface AdminPanelProps {
  year: number;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ year }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form states
  const [newTeamName, setNewTeamName] = useState("");
  const [newPlayerName, setNewPlayerName] = useState("");

  // Transaction form
  const [txnType, setTxnType] = useState<TransactionType>("kp_grant");
  const [txnTeam, setTxnTeam] = useState("");
  const [txnPlayer, setTxnPlayer] = useState("");
  const [txnKpAmount, setTxnKpAmount] = useState(5);
  const [txnDescription, setTxnDescription] = useState("");

  // User management form
  const [newUsername, setNewUsername] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserIsCommissioner, setNewUserIsCommissioner] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamList, playerList, userList] = await Promise.all([
        listTeams(),
        listPlayers(),
        adminListUsers(),
      ]);
      setTeams(teamList.sort((a, b) => a.name.localeCompare(b.name)));
      setPlayers(playerList.sort((a, b) => a.name.localeCompare(b.name)));
      setUsers(userList.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      await adminCreateUser(
        newUsername.trim(),
        newUserEmail.trim(),
        newUserPassword,
        newUserIsCommissioner
      );
      setNewUsername("");
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserIsCommissioner(false);
      setSuccess("User created successfully");
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    }
  };

  const handleToggleUserActive = async (user: AdminUser) => {
    setError(null);
    setSuccess(null);

    try {
      await adminSetUserActive(user.uuid, !user.is_active);
      setSuccess(`User ${user.is_active ? "deactivated" : "activated"} successfully`);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update user");
    }
  };

  const handleAddTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    try {
      await createTeam(newTeamName.trim());
      setNewTeamName("");
      setSuccess("Team created");
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create team");
    }
  };

  const handleAddPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPlayerName.trim()) return;

    try {
      await createPlayer(newPlayerName.trim());
      setNewPlayerName("");
      setSuccess("Player created");
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create player");
    }
  };

  const handleCreateTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const data: {
        transaction_type: TransactionType;
        year: number;
        team_uuid?: string;
        player_uuid?: string;
        kp_amount?: number;
        details?: Record<string, unknown>;
      } = {
        transaction_type: txnType,
        year,
      };

      if (txnTeam) data.team_uuid = txnTeam;
      if (txnPlayer) data.player_uuid = txnPlayer;
      if (txnType === "kp_grant") data.kp_amount = txnKpAmount;
      if (txnType === "pick_trade" && txnDescription) {
        data.details = { description: txnDescription };
      }

      await createTransaction(data);
      setSuccess("Transaction created");
      setTxnTeam("");
      setTxnPlayer("");
      setTxnDescription("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create transaction"
      );
    }
  };

  if (loading) {
    return <div className="loading">Loading admin panel...</div>;
  }

  return (
    <div>
      <h2>Admin Panel</h2>

      {error && (
        <div className="error" style={{ marginBottom: "1rem" }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: "1rem", background: "none", border: "none" }}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div
          style={{
            padding: "0.75rem",
            backgroundColor: "rgba(34, 139, 34, 0.1)",
            border: "1px solid var(--color-success)",
            borderRadius: "var(--radius-md)",
            color: "var(--color-success)",
            marginBottom: "1rem",
          }}
        >
          {success}
          <button
            onClick={() => setSuccess(null)}
            style={{ marginLeft: "1rem", background: "none", border: "none" }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "1.5rem",
        }}
      >
        {/* Add Team */}
        <div className="card">
          <h3>Add Team</h3>
          <form onSubmit={handleAddTeam}>
            <div className="form-group">
              <label className="form-label">Team Name</label>
              <input
                type="text"
                className="form-input"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="e.g., Middle Cyclone"
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Add Team
            </button>
          </form>
        </div>

        {/* Add Player */}
        <div className="card">
          <h3>Add Player</h3>
          <form onSubmit={handleAddPlayer}>
            <div className="form-group">
              <label className="form-label">Player Name</label>
              <input
                type="text"
                className="form-input"
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                placeholder="e.g., Shohei Ohtani"
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Add Player
            </button>
          </form>
        </div>

        {/* Create Transaction */}
        <div className="card" style={{ gridColumn: "span 2" }}>
          <h3>Create Transaction ({year})</h3>
          <form onSubmit={handleCreateTransaction}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "1rem",
              }}
            >
              <div className="form-group">
                <label className="form-label">Transaction Type</label>
                <select
                  className="form-select"
                  value={txnType}
                  onChange={(e) => setTxnType(e.target.value as TransactionType)}
                >
                  <option value="kp_grant">KP Grant</option>
                  <option value="lifetime_keeper">Lifetime Keeper</option>
                  <option value="one_year_keeper">One-Year Keeper</option>
                  <option value="pick_trade">Pick Trade</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Team</label>
                <select
                  className="form-select"
                  value={txnTeam}
                  onChange={(e) => setTxnTeam(e.target.value)}
                >
                  <option value="">Select team...</option>
                  {teams.map((team) => (
                    <option key={team.uuid} value={team.uuid}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>

              {(txnType === "lifetime_keeper" ||
                txnType === "one_year_keeper") && (
                <div className="form-group">
                  <label className="form-label">Player</label>
                  <select
                    className="form-select"
                    value={txnPlayer}
                    onChange={(e) => setTxnPlayer(e.target.value)}
                  >
                    <option value="">Select player...</option>
                    {players.map((player) => (
                      <option key={player.uuid} value={player.uuid}>
                        {player.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {txnType === "kp_grant" && (
                <div className="form-group">
                  <label className="form-label">KP Amount</label>
                  <input
                    type="number"
                    className="form-input"
                    value={txnKpAmount}
                    onChange={(e) => setTxnKpAmount(Number(e.target.value))}
                    min={1}
                    max={10}
                  />
                </div>
              )}

              {txnType === "pick_trade" && (
                <div className="form-group" style={{ gridColumn: "span 2" }}>
                  <label className="form-label">Trade Description</label>
                  <input
                    type="text"
                    className="form-input"
                    value={txnDescription}
                    onChange={(e) => setTxnDescription(e.target.value)}
                    placeholder="e.g., Middle Cyclone trades 2025 3rd round pick to La Mancha for 2025 5th round pick"
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ marginTop: "1rem" }}
            >
              Create Transaction
            </button>
          </form>
        </div>
      </div>

      {/* User Management */}
      <div style={{ marginTop: "2rem" }}>
        <h3>User Management</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "1.5rem",
            marginTop: "1rem",
          }}
        >
          {/* Create User Form */}
          <div className="card">
            <h4>Create User</h4>
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  className="form-input"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  required
                  minLength={3}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Temporary Password</label>
                <input
                  type="text"
                  className="form-input"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              <div className="form-group">
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <input
                    type="checkbox"
                    checked={newUserIsCommissioner}
                    onChange={(e) => setNewUserIsCommissioner(e.target.checked)}
                  />
                  Commissioner
                </label>
              </div>
              <button type="submit" className="btn btn-primary">
                Create User
              </button>
            </form>
          </div>

          {/* User List */}
          <div className="card" style={{ gridColumn: "span 2" }}>
            <h4>Users ({users.length})</h4>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>Username</th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>Email</th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>Role</th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>Status</th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>Last Login</th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.uuid}
                      style={{ borderBottom: "1px solid var(--color-border)" }}
                    >
                      <td style={{ padding: "0.5rem" }}>{user.username}</td>
                      <td style={{ padding: "0.5rem" }}>{user.email || "-"}</td>
                      <td style={{ padding: "0.5rem" }}>
                        {user.is_commissioner ? "Commissioner" : "Member"}
                      </td>
                      <td style={{ padding: "0.5rem" }}>
                        <span
                          style={{
                            color: user.is_active
                              ? "var(--color-success)"
                              : "var(--color-error)",
                          }}
                        >
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ padding: "0.5rem", fontSize: "0.875rem" }}>
                        {user.last_login
                          ? new Date(user.last_login).toLocaleDateString()
                          : "Never"}
                      </td>
                      <td style={{ padding: "0.5rem" }}>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                          onClick={() => handleToggleUserActive(user)}
                        >
                          {user.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Existing data */}
      <div style={{ marginTop: "2rem" }}>
        <h3>Teams ({teams.length})</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {teams.map((team) => (
            <span
              key={team.uuid}
              style={{
                padding: "0.25rem 0.75rem",
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
              }}
            >
              {team.name}
            </span>
          ))}
        </div>

        <h3 style={{ marginTop: "1.5rem" }}>Players ({players.length})</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {players.map((player) => (
            <span
              key={player.uuid}
              style={{
                padding: "0.25rem 0.75rem",
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
              }}
            >
              {player.name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
